import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Download, Loader2, FileText, CheckCircle2 } from "lucide-react";
import { http } from "@/lib/api";

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      const res = await http.post("/leads/newsletter", { email, source: "lead_magnet_home" });
      setDone(true);
      toast.success("Guide downloading — enjoy!");
      // Trigger PDF download
      const url = `${http.defaults.baseURL}${res.data.download_url || "/lead-magnet/pdf"}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = "StudyPoland-Starter-Guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-pad" data-testid="home-lead-magnet">
      <div className="container-sp">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-[2rem] border border-[#E5E3D8] bg-white p-8 md:p-16 grid md:grid-cols-12 gap-10 items-center"
        >
          <div
            aria-hidden
            className="absolute -top-24 -right-24 w-80 h-80 rounded-full blur-3xl opacity-20"
            style={{ background: "radial-gradient(circle, #D7141A 0%, transparent 70%)" }}
          />

          <div className="md:col-span-7 relative">
            <div className="overline">Free guide</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1B2A] mt-3 leading-tight">
              How to study in Poland without expensive mistakes.
            </h2>
            <p className="text-[#4A5568] mt-5 leading-relaxed max-w-lg">
              A concise, practical starter guide — the universities worth exploring,
              realistic costs, required documents and the visa basics — written
              from fifteen years of on-the-ground experience.
            </p>

            {done ? (
              <div className="mt-7 flex items-center gap-3 text-[#0D1B2A]" data-testid="lead-magnet-success">
                <CheckCircle2 className="w-5 h-5 text-[#D7141A]" />
                <span>Your download has started — check your browser.</span>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="lead-magnet-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="input-sp flex-1"
                  data-testid="lead-magnet-email"
                  required
                />
                <button type="submit" disabled={loading} className="btn-primary whitespace-nowrap justify-center" data-testid="lead-magnet-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download the Guide
                </button>
              </form>
            )}

            <p className="text-xs text-[#718096] mt-3">Instant download · no spam.</p>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative rounded-2xl bg-gradient-to-br from-[#0D1B2A] to-[#1B2A3D] text-white p-8 md:p-10 shadow-xl">
              <FileText className="w-8 h-8 text-[#E5444A] mb-5" />
              <div className="overline text-[#E5444A]">Starter guide · 2026</div>
              <h3 className="font-serif text-2xl mt-3 leading-tight">
                How to Study in Poland Without Expensive Mistakes
              </h3>
              <ul className="mt-6 space-y-2 text-sm text-white/80">
                <li>· Choosing your university wisely</li>
                <li>· Tuition, scholarships & living costs</li>
                <li>· Required documents & apostille</li>
                <li>· Visa process, step by step</li>
                <li>· Arrival & first 30 days checklist</li>
              </ul>
              <div className="mt-8 text-xs text-white/60 font-mono">32 pages · PDF</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
